<template>
  <div
    ref="carousel"
    :class="{ 'anti-content': !isWideScreen }"
    class="article-carousel"
    @mouseenter="pauseAnimation"
    @mouseleave="playAnimation">
    <div class="article-carousel__controls-wrapper">
      <h2 class="article-carousel__title">
        the most popular articles
      </h2>
      <div
        v-if="isAvailableScrollEvent && isWideScreen"
        class="article-carousel__controls">
        <button
          :disabled="isBlockControls"
          class="article-carousel__controls-button"
          @click="prev">
          <arrowhead-icon class="arrowhead-icon" />
        </button>
        <button
          :disabled="isBlockControls"
          class="button article-carousel__controls-button"
          @click="next">
          <arrowhead-icon class="arrowhead-icon" />
        </button>
      </div>
    </div>
    <div
      :class="{ 'anti-content': !isWideScreen }"
      class="article-carousel__list-wrapper">
      <ul
        ref="carouselList"
        :class="{
          'grabbing': isGrabbing,
          'grab': isAvailableScrollEvent,
          'hide-clones': !isAvailableScrollEvent,
        }"
        class="article-carousel__list"
        @mousedown="scrollStart"
        @wheel.shift="wheelHandler"
        @touchstart="scrollStart($event, true)"
        @touchmove="throttledScrollAction($event, true)"
        @touchend="scrollEnd($event, true)"
        @click.capture="stopClickEvent">
        <article-card
          v-for="(item, index) in articles"
          :key="index"
          :is-full="false"
          :article="item"
          class="article-carousel__item" />
      </ul>
    </div>
  </div>
</template>

<script>
import ArticleCard from 'Root/pages/blog/components/ArticleCard.vue';
import ArrowheadIcon from 'Components/ui/Icons/ArrowheadIcon.vue';
import { mapState } from 'vuex';
import { _throttle } from 'Root/helpers/tools';

const CONTAINER_MAX_WIDTH = 1680;
const MOBILE_GAP = 18;
const MAX_SLIDES_ON_DESKTOP = 7;

let posXEnd = 0;
let posXStart = 0;
let posScreenXStart = 0; // нужно для отмены клик ивента по карточке в случае скролла

let numberOfSlides;
let slideNodes;

let intervalID;
let unwatch;

export default {
  name: 'ArticleCarouselVanilla',

  components: {
    ArticleCard,
    ArrowheadIcon,
  },

  props: {
    articles: {
      type: Array,
      required: true,
    },

    autoScroll: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      carouselTranslate: 0,
      isStopAnimation: true, // флаг для блока автоскролла
      animationDelay: 3000, // время между перелистыванием слайдов при автоскролле
      gap: MOBILE_GAP,
      slideWidth: 208,
      activeSlideIndex: 0,
      isGrabbing: false, // флаг для изменения курсора
      carouselWidth: null,
      isBlockControls: false,
      needStopPropagationForClick: null,
    };
  },

  computed: {
    ...mapState(['userDevice']),

    throttledScrollAction() {
      return _throttle((e, isTouch) => {
        this.scrollAction(e, isTouch);
      }, 4);
    },

    isWideScreen() {
      return this.userDevice.innerWidth >= CONTAINER_MAX_WIDTH;
    },

    translateStep() {
      return this.slideWidth + this.gap;
    },

    maxScrollTranslate() {
      if (this.isWideScreen) {
        return this.translateStep * (numberOfSlides * 2);
      }

      return this.translateStep * numberOfSlides * 2; // ?
    },

    minScrollTranslate() {
      if (this.isWideScreen) {
        return this.translateStep * (numberOfSlides - this.maxSlidesPerView - 1);
      }

      return 0;
    },

    maxSlidesPerView() {
      const paddingCoef = 0.03;

      if (this.isWideScreen) {
        return MAX_SLIDES_ON_DESKTOP;
      }

      return Math.ceil((this.userDevice.innerWidth * (1 - paddingCoef) + this.gap) / this.translateStep);
    },

    isAvailableScrollEvent() {
      return this.articles.length >= this.maxSlidesPerView;
    },
  },

  watch: {
    carouselWidth(newValue) {
      this.$refs.carouselList.style.width = newValue;
    },

    isAvailableScrollEvent(newValue) {
      if (!newValue) {
        this.carouselWidth = null;
      } else {
        this.carouselWidth = this.translateStep * numberOfSlides * 3 - this.gap;
      }
    },
  },

  mounted() {
    numberOfSlides = this.articles.length;
    this.cloneSlides();

    this.$nextTick(() => {
      if (this.isAvailableScrollEvent) {
        if (this.isWideScreen) {
          this.wideScreenSetup();
        } else {
          this.narrowScreenSetup();
        }

        if (this.autoScroll) {
          this.setAutoScroll();
        }
      }

      // ставлю вотчер здесь потому что юзаю вызов сетапов на nextTick
      unwatch = this.$watch('isWideScreen', (newVal) => {
        if (this.articles.length > this.maxSlidesPerView) {
          if (newVal) {
            this.wideScreenSetup();
          } else {
            this.narrowScreenSetup();
          }
        }
      });
    });
  },

  beforeDestroy() {
    unwatch();
    // window.onblur = null;
    // window.onfocus = null;
    clearInterval(intervalID);
  },

  methods: {
    cloneSlides() {
      slideNodes = this.$refs.carouselList.querySelectorAll('.article-carousel__item');
      const lastSlideIndex = this.articles.length - 1;

      let index = 0;
      while (index < numberOfSlides) {
        const cloneToInsertAfterBegin = slideNodes[lastSlideIndex - index].cloneNode(true);
        const cloneToInsertBeforeEnd = slideNodes[index].cloneNode(true);

        cloneToInsertAfterBegin.classList.add('article-carousel__item--begin-clone');
        cloneToInsertBeforeEnd.classList.add('article-carousel__item--end-clone');

        this.$refs.carouselList.insertAdjacentElement('afterbegin', cloneToInsertAfterBegin);
        this.$refs.carouselList.insertAdjacentElement('beforeend', cloneToInsertBeforeEnd);
        index += 1;
      }
    },

    // сетап для узких экранов
    narrowScreenSetup() {
      this.setSlideWidth();
      this.gap = MOBILE_GAP;
      this.activeSlideIndex = 0;
      this.commonSetup();
    },

    // сетап для широкиъ экранов, которые > 1680px
    wideScreenSetup() {
      const wrapperWidth = this.$refs.carousel.getBoundingClientRect().width;
      this.setSlideWidth();

      this.gap = (wrapperWidth - MAX_SLIDES_ON_DESKTOP * this.slideWidth) / (MAX_SLIDES_ON_DESKTOP - 1);
      this.$refs.carouselList.style.setProperty('--gap', `${this.gap}px`);
      this.activeSlideIndex = numberOfSlides;

      this.commonSetup();
    },

    // общие сетап для разных экранов
    async commonSetup() {
      this.carouselWidth = this.translateStep * numberOfSlides * 3 - this.gap;
      // чтобы при сетапе слайды вставали на нужное место без анимации
      this.$refs.carouselList.style.transitionDuration = '0ms';
      this.carouselTranslate = -this.translateStep * this.activeSlideIndex; // крутим на первый эл
      this.setTranslate();
    },

    setSlideWidth() {
      this.slideWidth = slideNodes[0].getBoundingClientRect().width;
    },

    setAutoScroll() {
      // window.onblur = this.pauseAnimation;
      // window.onfocus = this.playAnimation;
      this.isStopAnimation = false;

      intervalID = setInterval(() => {
        if (!this.isStopAnimation) {
          // оставляю один знак после запятой для погрешности дробных чисел, чтобы, если там ноль прошла проверка
          const roundedSlidePosition = Math.round((this.carouselTranslate / this.translateStep) * 10) / 10;
          const isRightPosition = Number.isSafeInteger(roundedSlidePosition);
          if (isRightPosition) {
            this.next();
          } else {
            this.settingRightPosition();
          }
        }
      }, this.animationDelay);
    },

    next() {
      if (this.isBlockControls) {
        return;
      }

      if (this.isAvailableScrollEvent) {
        this.isBlockControls = true;

        this.activeSlideIndex += 1;
        let needTranslateToOriginal;
        if (this.isWideScreen) {
          needTranslateToOriginal = this.activeSlideIndex > this.articles.length + MAX_SLIDES_ON_DESKTOP;
        } else {
          needTranslateToOriginal = this.activeSlideIndex > this.articles.length * 2; // ?
        }

        this.oneStepScroll(-1, needTranslateToOriginal);
      }
    },

    prev() {
      if (this.isBlockControls) {
        return;
      }

      if (this.isAvailableScrollEvent) {
        this.isBlockControls = true;
        this.activeSlideIndex -= 1;
        const needTranslateToOriginal = this.activeSlideIndex < this.maxSlidesPerView;

        this.oneStepScroll(1, needTranslateToOriginal);
      }
    },

    oneStepScroll(direction, needTranslateToOriginal) {
      this.$refs.carouselList.addEventListener('transitionend', () => {
        this.isBlockControls = false;
      });
      this.$refs.carouselList.style.transitionDuration = null;
      this.carouselTranslate += direction * this.translateStep;
      this.setTranslate();

      if (needTranslateToOriginal) { // перестановка с копий на оригиналы
        this.swapCloneToOriginal();
      }
    },

    setTranslate() {
      // eslint-disable-next-line max-len
      this.$refs.carouselList.style.transform = `translateX(${this.isAvailableScrollEvent ? this.carouselTranslate : 0}px)`;
    },

    swapCloneToOriginal() {
      this.$refs.carouselList.addEventListener('transitionend', () => {
        this.$refs.carouselList.style.transitionDuration = '0ms';
        const currentStep = Math.round(-this.carouselTranslate / this.translateStep);

        let newIndex;
        if (this.isWideScreen) {
          const sign = Math.sign(currentStep - this.maxSlidesPerView);
          newIndex = currentStep + -sign * numberOfSlides;
        } else {
          newIndex = currentStep - numberOfSlides;
        }

        if (this.isWideScreen || newIndex >= 0) {
          this.carouselTranslate = -this.translateStep * newIndex;
          this.activeSlideIndex = newIndex;
          this.setTranslate();
        }
      }, { once: true });
    },

    wheelHandler(e) {
      if (this.isAvailableScrollEvent) {
        this.$refs.carouselList.style.transitionDuration = null;
        const nextTranslateValue = this.carouselTranslate - e.deltaY;

        // если нам некуда больше крутить, то просто делаем выравнивание по текущей позиции
        if (this.checkTranslateValueToCorrectScroll(nextTranslateValue)) {
          this.carouselTranslate = nextTranslateValue;
          this.setTranslate();
        } else {
          this.settingRightPosition();
        }
      }
    },

    scrollStart(event, isTouch) {
      if (this.isAvailableScrollEvent) {
        this.isStopAnimation = true;
        this.$refs.carouselList.style.transitionDuration = '0ms';

        if (isTouch) {
          posXStart = event.touches[0].clientX;
        } else {
          event.preventDefault();
          posXStart = event.clientX;
          posScreenXStart = event.screenX; // нужно для отмены клик ивента по карточке в случае скролла
          document.onmouseup = this.scrollEnd;
          document.onmousemove = this.throttledScrollAction;

          this.isGrabbing = true;
        }
      }
    },

    scrollAction(event, isTouch) {
      if (this.isAvailableScrollEvent) {
        if (isTouch) {
          event.preventDefault();
          posXEnd = posXStart - event.touches[0].clientX;
          posXStart = event.touches[0].clientX;
        } else {
          posXEnd = posXStart - event.clientX;
          posXStart = event.clientX;
        }

        const nextTranslateValue = this.carouselTranslate - posXEnd;
        if (this.checkTranslateValueToCorrectScroll(nextTranslateValue)) {
          this.carouselTranslate = nextTranslateValue;
          this.setTranslate();
        }
      }
    },

    scrollEnd(e, isTouch) {
      if (!isTouch && e.screenX !== posScreenXStart) {
        this.needStopPropagationForClick = true;
      }

      this.$refs.carouselList.style.transitionDuration = null;
      this.isGrabbing = false;
      document.onmouseup = null;
      document.onmousemove = null;

      this.settingRightPosition();
    },

    stopClickEvent(e) {
      if (this.needStopPropagationForClick) {
        e.preventDefault();
        e.stopPropagation();
      }

      this.needStopPropagationForClick = false;
    },

    settingRightPosition() {
      const isForwardDirection = this.activeSlideIndex * this.translateStep < -this.carouselTranslate;

      let stepMultiplier;
      const currentStepBeforeRound = this.carouselTranslate / this.translateStep;
      if (isForwardDirection) {
        stepMultiplier = Math.floor(currentStepBeforeRound);
      } else {
        stepMultiplier = Math.ceil(currentStepBeforeRound);
      }

      this.carouselTranslate = this.translateStep * stepMultiplier;
      this.setTranslate();
      this.activeSlideIndex = -stepMultiplier;

      if (!this.isWideScreen) {
        // eslint-disable-next-line max-len
        const needTranslateToOriginalOnMobile = this.activeSlideIndex >= (this.articles.length + this.maxSlidesPerView - 1);
        if (needTranslateToOriginalOnMobile) {
          this.swapCloneToOriginal();
        }
      }
    },

    // блокаю прокрутку за пределы, если быстро скролят и не было времени на замену клонов на оригинал
    checkTranslateValueToCorrectScroll(value) {
      const isForwardDirection = this.carouselTranslate - value > 0;
      if (isForwardDirection && -value >= this.maxScrollTranslate) {
        return false;
      }

      if (!isForwardDirection && -value <= this.minScrollTranslate) {
        return false;
      }

      return true;
    },

    pauseAnimation() {
      this.isStopAnimation = true;
    },

    playAnimation() {
      this.isStopAnimation = false;
    },
  },
};
</script>

<style lang="scss">
  @media screen and (max-width: #{$size-container-max-width}){
    .article-carousel__item--begin-clone {
      order: 10;
    }
  }
</style>

<style lang="scss" scoped>
  .article-carousel {
    overflow: hidden;
  }

  .article-carousel__list-wrapper {
    @include hide-scrollbar;

    position: relative;
    overflow-x: hidden;
    padding-top: 2px; // чтобы не было обрезок
    padding-bottom: 2px;
  }

  .article-carousel__controls-wrapper {
    height: 32px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }

  .article-carousel__title {
    font-weight: 500;
    font-size: 13px;
    line-height: 15px;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .article-carousel__controls {
    display: flex;
  }

  .article-carousel__controls-button {
    --size: 32px;

    width: var(--size);
    height: var(--size);
    border-radius: 50%;
    display: flex;
    background-color: transparent;
    border: 1px solid $color--basic-dark-gray;
    justify-content: center;
    align-items: center;
    transition: background-color .3s ease-out;
    cursor: pointer;
    user-select: none;
    transform: rotate(90deg);

    &:hover,
    &:focus {
      background-color: $color--basic-blue;
    }

    &:active {
      background-color: rgba($color--basic-blue, .31);
    }

    &:last-child {
      transform: rotate(-90deg);
      margin-left: 10px;
    }
  }

  .arrowhead-icon {
    --width: 40%;
  }

  .article-carousel__list {
    display: grid;
    grid-auto-flow: column;
    position: relative;
    will-change: transform;
    grid-column-gap: 18px; // = MOBILE_GAP
    width: max-content;
    transition: transform 400ms ease-out;

    &.grab {
      cursor: grab;
    }

    &.grabbing {
      cursor: grabbing;
    }

    &.hide-clones {
      .article-carousel__item--begin-clone,
      .article-carousel__item--end-clone {
        display: none;
      }
    }
  }

  .article-carousel__item {
    width: 188px;
  }

  @media screen and #{$screen-more-or-1280} {
    .article-carousel__controls-wrapper {
      margin-bottom: 20px;
    }
  }

  @media screen and (min-width: #{$size-container-max-width}) {
    .article-carousel__list {
      --gap: 20px;

      grid-column-gap: var(--gap, 20px);
    }

    .article-carousel__item {
      width: 208px;
    }
  }
</style>
